import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import PriorityTrivialIcon from '@atlaskit/icon/core/priority-trivial';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize, fontSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { Row } from '../../../../../../../common/ui/styled';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../controllers/enable-sla-hierarchical-goals';
import { useFetchPrioritiesService } from '../../../../../../../services/fetch-priorities/index.tsx';
import messages from './messages';

type Props = {
	selectedPriority?: string;
	placeholder?: string;
	goalId?: string;
	metricId?: number;
};

type Option = {
	label: string;
	value: string;
	icon: string;
};

export const PriorityViewFieldComponent = ({
	selectedPriority,
	placeholder,
	goalId,
	metricId,
}: Props) => {
	const { formatMessage } = useIntl();
	const [{ values: options }] = useFetchPrioritiesService();
	const [{ isPriorityNameChanged }, { updateIsPriorityNameChanged }] =
		useSLAHierarchicalGoalsEnabled();

	if (placeholder) return <ReadViewContainerWithFF>{placeholder}</ReadViewContainerWithFF>;
	if (options.length === 0) return null;

	// Priority name having multiple words is always saved in double quotes.
	// Eg: `priority = "High/Medium"
	// And then the selected priority becomes '"High/Medium"' and matching it with the priority options doesnt give us anything
	// since the option is 'High/Medium', with no double quotes wrapped.
	// So, to avoid this, we need to trim those extra double quotes around.
	const selectedPriorityTrimmed = selectedPriority?.replace(/^"+|"+$/g, '') || '';

	const selectedOption = options.filter(
		(option: Option) => option.value === selectedPriorityTrimmed,
	);

	// If the project has `high`, medium` and `low` priorities
	// and `high` is selected. Later on, `high` is renamed to `highest` in priorities list, it breaks the UI
	// To handle this scenario, below code is added.
	const isPriorityChanged = selectedOption.length === 0 && selectedPriorityTrimmed.length > 0;

	if (isPriorityChanged && isPriorityNameChanged !== isPriorityChanged)
		updateIsPriorityNameChanged(metricId, goalId, isPriorityChanged);
	if (isPriorityChanged) {
		return (
			<ReadViewContainerWithFF>
				<PriorityContainer>
					<IconContainer>
						<PriorityTrivialIcon label="" LEGACY_size="small" color="currentColor" />
					</IconContainer>
					{selectedPriorityTrimmed}
					<Tooltip content={formatMessage(messages.invalidPriority)} position="top">
						<Button
							iconBefore={
								<ErrorIcon
									primaryColor={token('color.icon.danger', colors.R500)}
									size="medium"
									label=""
								/>
							}
							appearance="subtle"
							aria-label={formatMessage(messages.invalidPriority)}
						/>
					</Tooltip>
				</PriorityContainer>
			</ReadViewContainerWithFF>
		);
	}

	const [{ label, icon, value }] = selectedOption || [{ label: '', icon: '', value: '' }];

	return (
		<ReadViewContainerWithFF>
			{value === 'EMPTY' ? (
				<IconContainer>
					<PriorityTrivialIcon label="" LEGACY_size="small" color="currentColor" />
				</IconContainer>
			) : (
				<ImageIcon src={icon} alt="" />
			)}
			{label}
		</ReadViewContainerWithFF>
	);
};

export const PriorityViewField = PriorityViewFieldComponent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageIcon = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: 2 * gridSize,
	marginInlineEnd: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: 2 * gridSize,
	marginInlineEnd: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PriorityContainer = styled(Row)({
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadViewContainerWithFF = styled.td({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSize}px`,
	lineHeight: 1.25,
	wordBreak: 'break-word',
});
